import React, { useState, useRef, useEffect } from 'react';
import { LucideIcon, ChevronDown } from 'lucide-react';

interface Tab {
  id: string;
  label: string;
  icon?: LucideIcon;
  count?: number;
  isActive?: boolean;
}

interface TabNavigationProps {
  tabs: Tab[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  variant?: 'default' | 'compact';
  color?: string;
}

export function TabNavigation({ tabs, activeTab, onTabChange, variant = 'default', color }: TabNavigationProps) {
  const isCompact = variant === 'compact';
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const activeTabData = tabs.find(tab => tab.id === activeTab);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isDropdownOpen]);

  const handleTabClick = (tabId: string) => {
    onTabChange(tabId);
    setIsDropdownOpen(false);
  };

  const renderTabContent = (tab: Tab, isActive: boolean) => {
    const Icon = tab.icon;
    const isFeatureActive = tab.isActive === true;

    return (
      <>
        {Icon && <Icon className="w-4 h-4" />}
        <span>{tab.label}</span>
        {tab.count !== undefined && (
          <span
            style={color && isActive ? { backgroundColor: color, color: 'white' } : {}}
            className={`
              px-2 py-0.5 rounded-full text-xs font-medium
              ${color
                ? isActive
                  ? ''
                  : 'bg-gray-100 text-gray-600'
                : isActive
                  ? isFeatureActive
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-900 text-white'
                  : isFeatureActive
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-600'
              }
            `}
          >
            {tab.count}
          </span>
        )}
      </>
    );
  };

  return (
    <div className={`border-b border-gray-200 ${isCompact ? 'mb-4' : 'mb-6'}`}>
      {/* Mobile dropdown */}
      <div className="md:hidden relative -mb-px" ref={dropdownRef}>
        <button
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          style={color ? { borderBottomColor: color, color: color } : {}}
          className={`
            w-full flex items-center justify-between gap-2 px-4 py-3 border-b-2 font-medium text-sm
            ${color ? '' : 'border-gray-900 text-gray-900'}
          `}
        >
          <div className="flex items-center gap-2">
            {activeTabData && renderTabContent(activeTabData, true)}
          </div>
          <ChevronDown className={`w-4 h-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
        </button>

        {isDropdownOpen && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto">
            {tabs.map((tab) => {
              const isActive = activeTab === tab.id;
              const isFeatureActive = tab.isActive === true;

              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabClick(tab.id)}
                  className={`
                    w-full flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors text-left
                    ${isActive
                      ? isFeatureActive
                        ? 'bg-green-50 text-green-700'
                        : 'bg-gray-50 text-gray-900'
                      : 'text-gray-700 hover:bg-gray-50'
                    }
                  `}
                >
                  {renderTabContent(tab, isActive)}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Desktop tabs */}
      <nav className="hidden md:flex -mb-px gap-2 overflow-x-auto">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          const isFeatureActive = tab.isActive === true;

          return (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              style={color && isActive ? {
                borderBottomColor: color,
                color: color
              } : {}}
              className={`
                flex items-center gap-2 px-4 py-3 border-b-2 font-medium text-sm transition-colors whitespace-nowrap
                ${color
                  ? isActive
                    ? ''
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  : isActive
                    ? isFeatureActive
                      ? 'border-green-600 text-green-700'
                      : 'border-gray-900 text-gray-900'
                    : isFeatureActive
                      ? 'border-transparent text-green-600 hover:text-green-700 hover:border-green-300'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              {renderTabContent(tab, isActive)}
            </button>
          );
        })}
      </nav>
    </div>
  );
}
